// API Client for portal backend

import type {
  LoginResponse, VerifyResponse, DashboardData, RotateResponse,
  CheckoutConfig, PaddleConfig, EnterpriseLicense, EnterpriseLicenseResult, GenerateEnterpriseRequest
} from '../types';

const API_BASE = import.meta.env.VITE_API_URL || 'https://license.intrigsoft.com';

export class ApiClient {
  private getHeaders(includeAuth = false): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (includeAuth) {
      const token = localStorage.getItem('sessionToken');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  async requestLogin(email: string): Promise<LoginResponse> {
    const response = await fetch(`${API_BASE}/portal/auth/request-login`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      throw new Error('Failed to request login');
    }

    return response.json();
  }

  async verifyToken(token: string): Promise<VerifyResponse> {
    const response = await fetch(`${API_BASE}/portal/auth/verify?token=${token}`);

    if (!response.ok) {
      throw new Error('Invalid or expired token');
    }

    return response.json();
  }

  async logout(): Promise<void> {
    await fetch(`${API_BASE}/portal/auth/logout`, {
      method: 'POST',
      headers: this.getHeaders(true),
    });
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('userEmail');
  }

  async getDashboard(): Promise<DashboardData> {
    const response = await fetch(`${API_BASE}/portal/dashboard`, {
      headers: this.getHeaders(true),
    });

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('sessionToken');
        throw new Error('Session expired');
      }
      throw new Error('Failed to fetch dashboard');
    }

    return response.json();
  }

  async rotateLicense(licenseId: string): Promise<RotateResponse> {
    const response = await fetch(`${API_BASE}/portal/license/rotate`, {
      method: 'POST',
      headers: this.getHeaders(true),
      body: JSON.stringify({ licenseId }),
    });

    if (!response.ok) {
      throw new Error('Failed to rotate license');
    }

    return response.json();
  }

  async updateSubscriptionLabel(
    subscriptionId: string,
    name?: string | null,
    description?: string | null
  ): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE}/portal/subscription/${subscriptionId}`, {
      method: 'PATCH',
      headers: this.getHeaders(true),
      body: JSON.stringify({ name, description }),
    });

    if (!response.ok) {
      throw new Error('Failed to update subscription label');
    }

    return response.json();
  }

  async getPaddleConfig(): Promise<PaddleConfig> {
    const response = await fetch(`${API_BASE}/portal/paddle-config`);

    if (!response.ok) {
      throw new Error('Failed to get Paddle config');
    }

    return response.json();
  }

  async getCheckoutConfig(subscriptionId: string): Promise<CheckoutConfig> {
    const response = await fetch(`${API_BASE}/portal/subscription/${subscriptionId}/checkout-config`, {
      method: 'POST',
      headers: this.getHeaders(true),
    });

    if (!response.ok) {
      throw new Error('Failed to get checkout config');
    }

    return response.json();
  }
  async listEnterpriseLicenses(): Promise<{ licenses: EnterpriseLicense[] }> {
    const response = await fetch(`${API_BASE}/portal/enterprise/licenses`, {
      headers: this.getHeaders(true),
    });

    if (!response.ok) {
      throw new Error('Failed to list enterprise licenses');
    }

    return response.json();
  }

  async generateEnterpriseLicense(request: GenerateEnterpriseRequest): Promise<EnterpriseLicenseResult> {
    const response = await fetch(`${API_BASE}/portal/enterprise/generate`, {
      method: 'POST',
      headers: this.getHeaders(true),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error('Failed to generate enterprise license');
    }

    return response.json();
  }

  async renewEnterpriseLicense(id: string, durationDays: number): Promise<EnterpriseLicenseResult> {
    const response = await fetch(`${API_BASE}/portal/enterprise/${id}/renew`, {
      method: 'POST',
      headers: this.getHeaders(true),
      body: JSON.stringify({ durationDays }),
    });

    if (!response.ok) {
      throw new Error('Failed to renew enterprise license');
    }

    return response.json();
  }

  async requestEarlyAccess(
    email: string,
    tier: 'free' | 'pro',
    source?: string
  ): Promise<{ ok: boolean; message: string }> {
    const response = await fetch(`${API_BASE}/early-access/signup`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ email, tier, source }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error((data as { message?: string }).message || 'Failed to register for early access');
    }

    return data as { ok: boolean; message: string };
  }
}

export const api = new ApiClient();
